import 'react-datepicker/dist/react-datepicker.css'

import { USCurrency } from 'components/ReusableComponents/FormattedPrice'
import billableStyles from 'features/Invoice/BillableItems/BillableItems.module.scss'
import InvoiceInstructions from 'features/Invoice/InvoiceInstructions'
import InvoiceSummaryCard from 'features/Invoice/InvoiceSummaryCard'
import totalCardStyles from 'features/Invoice/TotalCard/TotalCard.module.scss'
import moment from 'moment'
import { useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import { useHistory } from 'react-router-dom'

import { ReactComponent as CloseIcon } from '../close.svg'
import invoiceStyles from '../Invoice.module.scss'
import InvoiceHeader from '../InvoiceHeader'
import { ReactComponent as PrintIcon } from '../print.svg'
import RevisionLink from '../RevisionLink'
import styles from './DynamicInvoice.module.scss'
import {
  DynamicInvoiceDefaultProps,
  DynamicInvoicePropTypes
} from './IDynamicInvoiceProps'

const defaultInvoiceDetails = {
  billingAddress: '',
  subsidiaryName: '',
  generatedOn: null,
  issuedOn: null,
  previousBalance: 0,
  paymentsAppliedSinceLastInvoice: 0,
  balanceForward: 0,
  invoiceAmount: 0,
  totalAmountDueWithPreviousBalance: 0,
  paymentDueDate: null,
  status: '',
  deliveredToName: '',
  subsidiaryInvoiceId: '',
  isAutoPayEnabled: false,
  autoPayChargeDate: null,
  totalFixedServices: 1,
  totalMessageFees: 0,
  customMessagesBeginDate: null,
  customMessagesEndDate: null,
  subsidiarySubscriptions: [],
  beginDate: null,
  endDate: null,
  totalTax: 0,
  discount: 0
}

const DynamicInvoice = ({
  invoiceDetails = defaultInvoiceDetails,
  taxRate = 9.5,
  messagesWithAdditionalCharges,
  initialStartDate,
  initialEndDate,
  initialInvoiceDate,
  initialNewChargesOn,
  initialInvoiceId,
  initialHotels
}) => {
  const history = useHistory()

  const handlePrint = () => {
    window.print()
  }

  const handleClose = () => {
    history.push('/billing')
  }

  const [startDate, setStartDate] = useState(
    initialStartDate ? moment(initialStartDate) : moment().startOf('month')
  )
  const [endDate, setEndDate] = useState(
    initialEndDate ? moment(initialEndDate) : moment().endOf('month')
  )
  const [invoiceDate, setInvoiceDate] = useState(
    initialInvoiceDate
      ? moment(initialInvoiceDate)
      : moment().subtract(1, 'month').startOf('month')
  )
  const [newChargesOn, setNewChargesOn] = useState(
    initialNewChargesOn ? moment(initialNewChargesOn) : moment().startOf('month')
  )
  const [invoiceId, setInvoiceId] = useState(initialInvoiceId || '')
  const [hotels, setHotels] = useState(initialHotels || [])

  // Invoice To / Bill To fields
  const [invoiceToName, setInvoiceToName] = useState('')
  const [invoiceToAddress, setInvoiceToAddress] = useState('')
  const [billToName, setBillToName] = useState('')
  const [billToAddress, setBillToAddress] = useState('')

  // Balance fields
  const [previousBalance, setPreviousBalance] = useState(0)
  const [paymentsApplied, setPaymentsApplied] = useState(0)
  const [balanceForward, setBalanceForward] = useState(0)

  const totalIncludingTax = useMemo(() => {
    return hotels.reduce((acc, hotel) => {
      return acc + hotel.price + (hotel.price * hotel.taxRate) / 100
    }, 0)
  }, [hotels])

  // Map hotels to subsidiarySubscriptions format for BillableItems
  const mappedSubscriptions = hotels.map(hotel => ({
    plan: 'DirectfulCore',
    planRate: hotel.price,
    startDate: startDate?.format?.('YYYY-MM-DD') || null,
    endDate: endDate?.format?.('YYYY-MM-DD') || null,
    name: hotel.name,
    taxRate: hotel.taxRate
  }))

  const totalDue = totalIncludingTax + balanceForward

  const safeInvoiceDetails = {
    ...defaultInvoiceDetails,
    ...invoiceDetails,
    subsidiarySubscriptions: mappedSubscriptions,
    totalFixedServices: hotels.length > 0 ? totalIncludingTax : 0,
    // Map UI controls to invoiceDetails fields used by InvoiceSummaryCard
    generatedOn: newChargesOn.toISOString(),
    issuedOn: invoiceDate.toISOString(),
    subsidiaryInvoiceId: invoiceId,
    invoiceAmount: totalIncludingTax,
    totalAmountDueWithPreviousBalance: totalDue,
    paymentDueDate: moment(invoiceDate).add(30, 'days').toISOString(),
    beginDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    // Invoice To / Bill To fields
    deliveredToName: invoiceToName,
    invoiceAddress: invoiceToAddress,
    subsidiaryName: billToName,
    billingAddress: billToAddress,
    // Balance fields
    previousBalance: previousBalance,
    paymentsAppliedSinceLastInvoice: paymentsApplied,
    balanceForward: balanceForward
  }

  const handleAddItem = () => {
    setHotels([...hotels, { name: '', price: 0, taxRate: 0 }])
  }

  const handleRemoveItem = index => {
    setHotels(hotels.filter((_, i) => i !== index))
  }

  const handleItemChange = (index, field, value) => {
    const updatedHotels = [...hotels]
    if (field === 'price' || field === 'taxRate') {
      updatedHotels[index][field] = parseFloat(value) || 0
    } else {
      updatedHotels[index][field] = value
    }
    setHotels(updatedHotels)
  }

  const formattedInvoiceDate = invoiceDate.format('YYYY-MM-DD')
  const formattedNewChargesOn = newChargesOn.format('YYYY-MM-DD')

  return (
    <div className={styles.pageWrapper}>
      {/* Control Panel - Hidden on Print */}
      <div className={styles.controlPanel}>
        <div className={styles.controlPanelHeader}>
          <h3 className={styles.controlPanelTitle}>Dynamic Invoice</h3>
          <div className={styles.actionButtons}>
            <button
              type="button"
              className={styles.printButton}
              onClick={handlePrint}
              title="Print invoice"
            >
              <PrintIcon />
              Print
            </button>
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              title="Close"
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Date Pickers Row */}
        <div className={styles.dateRow}>
          <div className={styles.dateField}>
            <label className={styles.fieldLabel}>Start Date</label>
            <div className={styles.datepickerWrapper}>
              <DatePicker
                selected={startDate.toDate()}
                onChange={date => setStartDate(moment(date))}
                dateFormat="yyyy-MM-dd"
              />
            </div>
          </div>

          <div className={styles.dateField}>
            <label className={styles.fieldLabel}>End Date</label>
            <div className={styles.datepickerWrapper}>
              <DatePicker
                selected={endDate.toDate()}
                onChange={date => setEndDate(moment(date))}
                dateFormat="yyyy-MM-dd"
              />
            </div>
          </div>

          <div className={styles.dateField}>
            <label className={styles.fieldLabel}>Invoice Date</label>
            <div className={styles.datepickerWrapper}>
              <DatePicker
                selected={invoiceDate.toDate()}
                onChange={date => setInvoiceDate(moment(date))}
                dateFormat="yyyy-MM-dd"
              />
            </div>
          </div>

          <div className={styles.dateField}>
            <label className={styles.fieldLabel}>New Charges On</label>
            <div className={styles.datepickerWrapper}>
              <DatePicker
                selected={newChargesOn.toDate()}
                onChange={date => setNewChargesOn(moment(date))}
                dateFormat="yyyy-MM-dd"
              />
            </div>
          </div>
        </div>

        {/* Invoice ID */}
        <div className={styles.invoiceIdRow}>
          <label className={styles.fieldLabel}>Invoice ID</label>
          <input
            type="text"
            className={styles.invoiceIdInput}
            value={invoiceId}
            onChange={e => setInvoiceId(e.target.value)}
            placeholder="Enter invoice ID"
          />
        </div>

        {/* Balance Fields */}
        <div className={styles.balanceRow}>
          <div className={styles.balanceField}>
            <label className={styles.fieldLabel}>Previous Balance</label>
            <input
              type="number"
              className={styles.balanceInput}
              value={previousBalance}
              onChange={e => setPreviousBalance(parseFloat(e.target.value) || 0)}
              placeholder="0.00"
              step="0.01"
            />
          </div>
          <div className={styles.balanceField}>
            <label className={styles.fieldLabel}>Payments Applied</label>
            <input
              type="number"
              className={styles.balanceInput}
              value={paymentsApplied}
              onChange={e => setPaymentsApplied(parseFloat(e.target.value) || 0)}
              placeholder="0.00"
              step="0.01"
            />
          </div>
          <div className={styles.balanceField}>
            <label className={styles.fieldLabel}>Balance Forward</label>
            <input
              type="number"
              className={styles.balanceInput}
              value={balanceForward}
              onChange={e => setBalanceForward(parseFloat(e.target.value) || 0)}
              placeholder="0.00"
              step="0.01"
            />
          </div>
        </div>

        {/* Invoice To / Bill To Section */}
        <div className={styles.addressSection}>
          <div className={styles.addressColumn}>
            <h4 className={styles.itemsSectionTitle}>Invoice To</h4>
            <div className={styles.addressField}>
              <label className={styles.fieldLabel}>Name</label>
              <input
                type="text"
                className={styles.itemInput}
                value={invoiceToName}
                onChange={e => setInvoiceToName(e.target.value)}
                placeholder="Recipient name"
              />
            </div>
            <div className={styles.addressField}>
              <label className={styles.fieldLabel}>Address</label>
              <textarea
                className={styles.addressTextarea}
                value={invoiceToAddress}
                onChange={e => setInvoiceToAddress(e.target.value)}
                placeholder="Street address, City, State, ZIP"
                rows={3}
              />
            </div>
          </div>
          <div className={styles.addressColumn}>
            <h4 className={styles.itemsSectionTitle}>Bill To</h4>
            <div className={styles.addressField}>
              <label className={styles.fieldLabel}>Name</label>
              <input
                type="text"
                className={styles.itemInput}
                value={billToName}
                onChange={e => setBillToName(e.target.value)}
                placeholder="Company/Person name"
              />
            </div>
            <div className={styles.addressField}>
              <label className={styles.fieldLabel}>Address</label>
              <textarea
                className={styles.addressTextarea}
                value={billToAddress}
                onChange={e => setBillToAddress(e.target.value)}
                placeholder="Street address, City, State, ZIP"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Items Section */}
        <div className={styles.itemsSection}>
          <h4 className={styles.itemsSectionTitle}>Billable Items</h4>

          {hotels.length > 0 && (
            <div className={styles.itemHeader}>
              <span className={`${styles.itemHeaderLabel} ${styles.nameLabel}`}>
                Name
              </span>
              <span className={`${styles.itemHeaderLabel} ${styles.priceLabel}`}>
                Price
              </span>
              <span className={`${styles.itemHeaderLabel} ${styles.taxLabel}`}>
                Tax Rate (%)
              </span>
              <span className={`${styles.itemHeaderLabel} ${styles.actionLabel}`} />
            </div>
          )}

          <div className={styles.itemsList}>
            {hotels.length === 0 ? (
              <div className={styles.emptyState}>
                No items added yet. Click Add Item to get started.
              </div>
            ) : (
              hotels.map((hotel, index) => (
                <div key={index} className={styles.itemRow}>
                  <div className={styles.itemName}>
                    <input
                      type="text"
                      className={styles.itemInput}
                      value={hotel.name}
                      onChange={e => handleItemChange(index, 'name', e.target.value)}
                      placeholder="Item name"
                    />
                  </div>
                  <div className={styles.itemPrice}>
                    <input
                      type="number"
                      className={styles.itemInputSmall}
                      value={hotel.price}
                      onChange={e => handleItemChange(index, 'price', e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className={styles.itemTaxRate}>
                    <input
                      type="number"
                      className={styles.itemInputSmall}
                      value={hotel.taxRate}
                      onChange={e => handleItemChange(index, 'taxRate', e.target.value)}
                      placeholder="0"
                      step="0.01"
                    />
                  </div>
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => handleRemoveItem(index)}
                    title="Remove item"
                  >
                    &times;
                  </button>
                </div>
              ))
            )}
          </div>

          <button type="button" className={styles.addButton} onClick={handleAddItem}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            Add Item
          </button>
        </div>
      </div>

      {/* Invoice Display - Always Visible */}
      <div className={invoiceStyles.invoice} id="invoice">
        <div className={invoiceStyles.pageOne}>
          <InvoiceHeader />
          <InvoiceSummaryCard
            invoiceDetails={safeInvoiceDetails}
            total={totalIncludingTax}
            invoicedOn={formattedInvoiceDate}
            newChargesOn={formattedNewChargesOn}
            id={invoiceId}
          />
          <InvoiceInstructions invoiceDetails={safeInvoiceDetails} />
          {/* Custom Billable Items Display */}
          {hotels.length > 0 && (
            <div className={billableStyles.billableItems}>
              <p className={billableStyles.billableItemsTitle}>Billable items</p>
              {hotels.map((hotel, index) => (
                <div key={index} className={billableStyles.billableItem}>
                  <div className={billableStyles.billableItemText}>
                    <p className={billableStyles.billableItemTitle}>
                      {hotel.name || `Item ${index + 1}`}
                    </p>
                    <p className={billableStyles.billableItemDescription}>
                      For {startDate.format('MMM DD, YYYY')} -{' '}
                      {endDate.format('MMM DD, YYYY')}
                      {hotel.taxRate > 0 && ` (${hotel.taxRate}% tax)`}
                    </p>
                  </div>
                  <div className={billableStyles.billableItemPrice}>
                    <USCurrency withCode>{hotel.price}</USCurrency>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Simple Total Card */}
          <div className={totalCardStyles.totalCard}>
            <div className={totalCardStyles.totalCardRow}>
              <div className={totalCardStyles.totalCardName}>Subtotal</div>
              <div className={totalCardStyles.totalCardValue}>
                <USCurrency withCode>{totalIncludingTax}</USCurrency>
              </div>
            </div>
            <div className={totalCardStyles.totalCardRow}>
              <div className={totalCardStyles.totalCardName}>Balance forward</div>
              <div className={totalCardStyles.totalCardValue}>
                <USCurrency withCode>{balanceForward}</USCurrency>
              </div>
            </div>
            <div className={totalCardStyles.totalCardRowHighlighted}>
              <div className={totalCardStyles.totalCardName}>Total billed</div>
              <div className={totalCardStyles.totalCardValue}>
                <USCurrency withCode>{totalDue}</USCurrency>
              </div>
            </div>
          </div>
          <RevisionLink invoiceDetails={safeInvoiceDetails} />
        </div>
      </div>
    </div>
  )
}

DynamicInvoice.propTypes = DynamicInvoicePropTypes
DynamicInvoice.defaultProps = DynamicInvoiceDefaultProps

export default DynamicInvoice
